import React, { useState, useEffect, createContext, useContext } from 'react';

// Create a context for our error handler
export const ErrorContext = createContext({
  addError: () => {},
  removeError: () => {},
  errors: [],
});

// Hook to use the error context
export const useError = () => useContext(ErrorContext);

// Toast notification component
const Toast = ({ message, type = 'error', onClose, id }) => {
  // Auto-dismiss after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [id, onClose]);
  
  // Determine color based on type
  const bgColor = 
    type === 'error' ? 'bg-red-500' :
    type === 'success' ? 'bg-green-500' :
    type === 'warning' ? 'bg-yellow-500' :
    'bg-blue-500';
  
  return (
    <div className={`${bgColor} text-white rounded-lg shadow-lg p-4 mb-2 max-w-md`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'error' && (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          {type === 'success' && (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          )}
          {type === 'warning' && (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          {type === 'info' && (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <div>{message}</div>
        </div>
        <button onClick={() => onClose(id)} className="ml-4 text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Backend error `detail`/messages can arrive as a plain string or as a
// structured object ({message, error_code, ...}) - never render the object
// directly (React error #31: objects are not valid as a child).
const toDisplayText = (value, fallback = 'Une erreur est survenue.') => {
  if (value == null) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value.message || fallback;
  return String(value);
};

// Main error handler provider component
export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);

  // Add a new error
  const addError = (message, type = 'error') => {
    const id = Date.now().toString();
    setErrors(prev => [...prev, { id, message: toDisplayText(message), type }]);
    return id;
  };
  
  // Remove an error by id
  const removeError = (id) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  };
  
  // Add convenient shorthand methods for different notification types
  const showError = (message) => addError(message, 'error');
  const showSuccess = (message) => addError(message, 'success');
  const showWarning = (message) => addError(message, 'warning');
  const showInfo = (message) => addError(message, 'info');
  
  return (
    <ErrorContext.Provider value={{ 
      addError, 
      removeError, 
      errors,
      showError,
      showSuccess,
      showWarning,
      showInfo
    }}>
      {children}
      
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50">
        {errors.map(error => (
          <Toast
            key={error.id}
            id={error.id}
            message={error.message}
            type={error.type}
            onClose={removeError}
          />
        ))}
      </div>
    </ErrorContext.Provider>
  );
};

export default ErrorProvider;